// frontend/src/components/auditor/AuditorFinancials.js
// Invoices, payments, invoice journals, and aged receivables for audit review.

import React, { useState, useEffect, useCallback } from 'react';
import AuditorTable from './AuditorTable';
import { fetchAllPages, fetchReport } from '../../utils/auditorApi';

const SUB_TABS = [
  { id: 'invoices', label: 'Invoices' },
  { id: 'payments', label: 'Payments' },
  { id: 'journals', label: 'Journals' },
  { id: 'summary', label: 'Financial Summary' },
];

const fmt = (v) => v != null ? Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—';
const fmtDate = (v) => v ? new Date(v).toLocaleDateString() : '—';

const StatusBadge = ({ status }) => {
  const colors = {
    Paid: 'bg-green-100 text-green-700',
    Overdue: 'bg-red-100 text-red-700',
    Sent: 'bg-blue-100 text-blue-700',
    Draft: 'bg-gray-100 text-gray-600',
    'Partially Paid': 'bg-amber-100 text-amber-700',
    'Written Off': 'bg-slate-100 text-slate-600',
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${colors[status] || 'bg-gray-100 text-gray-600'}`}>
      {status || '—'}
    </span>
  );
};

export default function AuditorFinancials({ authAxios, dateRange, showFormMessage }) {
  const [subTab, setSubTab] = useState('invoices');
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [journals, setJournals] = useState([]);
  const [financialSummary, setFinancialSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setFetchError(null);
    const [inv, pay, jour, fin] = await Promise.all([
      fetchAllPages(authAxios, 'invoices/'),
      fetchAllPages(authAxios, 'payments/'),
      fetchAllPages(authAxios, 'invoice-journals/'),
      fetchReport(authAxios, 'reports/financial/', { start_date: dateRange.startDate, end_date: dateRange.endDate }),
    ]);
    setInvoices(inv);
    setPayments(pay);
    setJournals(jour);
    setFinancialSummary(fin);
    // Warn user if all arrays came back empty (likely a permissions / network issue)
    if (inv.length === 0 && pay.length === 0 && jour.length === 0 && !fin) {
      setFetchError('No financial data was returned. Check the browser console for API errors.');
    }
    setLoading(false);
  }, [authAxios, dateRange]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-800">Financial Records</h2>
        <p className="text-sm text-gray-500 mt-1">Invoices, payments, journals, and financial summary.</p>
      </div>

      {fetchError && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-lg p-4 text-sm flex items-start gap-2">
          <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
          <span>{fetchError}</span>
        </div>
      )}

      {/* Sub-tabs */}
      <div className="flex space-x-1 bg-gray-100 rounded-lg p-1 w-fit">
        {SUB_TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setSubTab(t.id)}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              subTab === t.id ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Invoices */}
      {subTab === 'invoices' && (
        <AuditorTable
          title={`Invoices (${invoices.length})`}
          data={invoices}
          columns={[
            { key: 'invoice_number', label: 'Invoice #' },
            { key: 'client_name', label: 'Client', render: (v, row) => row.client_name || row.client_details?.name || '—' },
            { key: 'case_reference', label: 'Case', render: (v, row) => row.case_reference || row.case_details?.reference || '—' },
            { key: 'status', label: 'Status', render: (v) => <StatusBadge status={v} /> },
            { key: 'total_amount', label: 'Total (P)', render: (v) => fmt(v), className: 'text-right' },
            { key: 'amount_paid', label: 'Paid (P)', render: (v) => fmt(v), className: 'text-right' },
            { key: 'balance_due', label: 'Balance (P)', render: (v) => fmt(v), className: 'text-right' },
            { key: 'issue_date', label: 'Issued', render: (v) => fmtDate(v) },
            { key: 'due_date', label: 'Due', render: (v) => fmtDate(v) },
          ]}
          exportCsv={{ filename: 'invoices.csv' }}
          emptyMessage="No invoices found for the selected period."
        />
      )}

      {/* Payments */}
      {subTab === 'payments' && (
        <AuditorTable
          title={`Payments (${payments.length})`}
          data={payments}
          columns={[
            { key: 'receipt_number', label: 'Receipt #' },
            { key: 'invoice_number', label: 'Invoice #', render: (v, row) => row.invoice_number || row.invoice_details?.invoice_number || '—' },
            { key: 'payment_method', label: 'Method' },
            { key: 'amount', label: 'Amount (P)', render: (v) => fmt(v), className: 'text-right' },
            { key: 'payment_date', label: 'Date', render: (v) => fmtDate(v) },
            { key: 'reference', label: 'Reference' },
          ]}
          exportCsv={{ filename: 'payments.csv' }}
          emptyMessage="No payments found."
        />
      )}

      {/* Journals */}
      {subTab === 'journals' && (
        <AuditorTable
          title={`Invoice Journals (${journals.length})`}
          data={journals}
          columns={[
            { key: 'invoice_number', label: 'Invoice #', render: (v, row) => row.invoice_number || row.invoice_details?.invoice_number || '—' },
            { key: 'journal_type', label: 'Type', render: (v) => (
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                v === 'PAYMENT' ? 'bg-green-100 text-green-700' :
                v === 'WRITE_OFF' ? 'bg-slate-100 text-slate-600' :
                v === 'REFUND' ? 'bg-red-100 text-red-700' :
                v === 'CREDIT_NOTE' ? 'bg-purple-100 text-purple-700' :
                'bg-gray-100 text-gray-600'
              }`}>{v ? v.replace('_', ' ') : '—'}</span>
            )},
            { key: 'amount', label: 'Amount (P)', render: (v) => fmt(v), className: 'text-right' },
            { key: 'date', label: 'Date', render: (v) => fmtDate(v) },
            { key: 'notes', label: 'Notes' },
            { key: 'created_by_username', label: 'By', render: (v, row) => row.created_by_username || row.created_by_details?.username || '—' },
          ]}
          exportCsv={{ filename: 'invoice_journals.csv' }}
          emptyMessage="No journal entries found."
        />
      )}

      {/* Financial Summary */}
      {subTab === 'summary' && financialSummary && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Financial Summary</h3>
          <p className="text-sm text-gray-500 mb-6">{dateRange.startDate} to {dateRange.endDate}</p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {[
              { label: 'Total Invoiced', value: `P ${fmt(financialSummary.total_invoiced_amount)}`, color: 'text-blue-700' },
              { label: 'Paid Invoices', value: `P ${fmt(financialSummary.paid_invoices_amount)}`, color: 'text-green-700' },
              { label: 'Outstanding', value: `P ${fmt(financialSummary.outstanding_invoices_amount)}`, color: 'text-amber-700' },
              { label: 'Work-in-Progress', value: `P ${fmt(financialSummary.work_in_progress_amount)}`, color: 'text-purple-700' },
              { label: 'Total Billable Hours', value: fmt(financialSummary.total_billable_hours), color: 'text-indigo-700' },
              { label: 'Total Expenses', value: `P ${fmt(financialSummary.total_expenses)}`, color: 'text-red-700' },
              { label: 'Turnover excl. Group K', value: `P ${fmt(financialSummary.total_turnover_excl_group_k)}`, color: 'text-teal-700' },
            ].map((item, i) => (
              <div key={i} className="bg-gray-50 rounded-lg p-4">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">{item.label}</p>
                <p className={`text-xl font-bold ${item.color}`}>{item.value}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
